import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/mocks/categories";
import { articleMeta } from "@/mocks/articleMeta";
import { articlesEn } from "@/mocks/articlesEn";
import { articlesFr } from "@/mocks/articlesFr";
import { articlesEs } from "@/mocks/articlesEs";
import { articlesPl } from "@/mocks/articlesPl";
import type { ArticleData } from "@/types/article";

const articlesByLang: Record<string, Record<string, ArticleData>> = {
  en: articlesEn,
  fr: articlesFr,
  es: articlesEs,
  pl: articlesPl,
};

function resolveArticle(slug: string, lang: string): ArticleData | undefined {
  const langArticles = articlesByLang[lang] || articlesEn;
  return langArticles[slug] || articlesEn[slug];
}

export default function ArticleSection() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];

  const [featured, ...rest] = articleMeta;

  return (
    <section id="articles" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50 scroll-mt-20">
      <div className="max-w-[1280px] mx-auto">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">{t("articles_title")}</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-700">{t("articles_subtitle")}</p>
        </div>

        {/* Featured article */}
        {featured && (() => {
          const featuredData = resolveArticle(featured.slug, lang);
          if (!featuredData) return null;
          return (
            <Link
              to={`/articles/${featured.slug}`}
              className="mt-10 group rounded-lg border border-background-200 bg-background-50 overflow-hidden cursor-pointer hover:border-primary-300 transition-colors flex flex-col sm:flex-row"
            >
              <div className="w-full sm:w-80 shrink-0 h-48 sm:h-auto overflow-hidden">
                <img
                  src={featured.image}
                  alt={featuredData.title}
                  title={`${featuredData.title} - USFans Spreadsheet`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <span className="inline-flex w-fit px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold">
                  USFans Spreadsheet
                </span>
                <h3 className="mt-4 font-heading font-bold text-foreground-950 text-xl md:text-2xl">
                  {featuredData.title}
                </h3>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{featuredData.description}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
                  {t("article_read")}
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>
          );
        })()}

        {/* Remaining articles grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {rest.map((m) => {
            const data = resolveArticle(m.slug, lang);
            if (!data) return null;
            return (
              <Link
                key={m.slug}
                to={`/articles/${m.slug}`}
                className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden cursor-pointer hover:border-primary-300 transition-colors flex flex-col"
              >
                <div className="w-full h-40 overflow-hidden">
                  <img
                    src={m.image}
                    alt={data.title}
                    title={`${data.title} - USFans Spreadsheet`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-heading font-bold text-foreground-950 text-base leading-snug group-hover:text-primary-700 transition-colors">
                    {data.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">{data.description}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 mt-auto pt-3">
                    {t("article_read")}
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FinalCta() {
  const { t } = useTranslation();

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-primary-600">
      <div className="max-w-[1280px] mx-auto text-center">
        <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50">{t("final_title")}</h2>
        <p className="mt-4 text-sm md:text-base text-background-100 max-w-2xl mx-auto leading-relaxed">
          {t("final_subtitle")}
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-background-50 text-primary-700 font-semibold text-sm cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            {t("final_cta")}
            <i className="ri-arrow-right-up-line" />
          </a>
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md border border-background-50/60 text-background-50 font-semibold text-sm cursor-pointer hover:bg-primary-700 transition-colors whitespace-nowrap"
          >
            {t("final_secondary")}
            <i className="ri-external-link-line" />
          </a>
        </div>
      </div>
    </section>
  );
}